import { z } from "zod";

import { DbEnum } from "../../../../generated/prisma-enums";

/**
 * One deployment's chat-retention policy: what the daily retention sweep does to
 * that deployment's conversations once they have been inactive for `days` days.
 *
 * The row is `DeploymentRetentionPolicy`, at most one per deployment. A
 * deployment with no row has no policy, and reads as
 * {@link DEPLOYMENT_RETENTION_POLICY_DEFAULTS} with `configured: false` — never
 * as a 404, because "never configured" is the ordinary state of every
 * deployment rather than an error.
 */

/** A policy acting on conversations inactive for zero days would act on every conversation. */
export const RETENTION_POLICY_DAYS_MIN = 1;

/**
 * Ten years. Mirrors Echo's `retention.daysInDatabase` upper bound
 * (`shared/echo/config.schemas.ts`), the one other retention window the
 * platform lets an organization choose.
 */
export const RETENTION_POLICY_DAYS_MAX = 3650;

export const ChatRetentionActionSchema = z.enum(DbEnum.CHAT_RETENTION_ACTION_VALUES);

export type ChatRetentionActionValue = z.infer<typeof ChatRetentionActionSchema>;

export const DeploymentRetentionPolicyPathVarsSchema = z.object({
  id: z.string().uuid()
});

export type DeploymentRetentionPolicyPathVars = z.infer<
  typeof DeploymentRetentionPolicyPathVarsSchema
>;

/**
 * The body of `PUT /api/deployments/:id/retention-policy`.
 *
 * - `enabled` and `days` are always sent: they are the policy.
 * - `action` is required once the policy is enabled. A DISABLED write may omit
 *   it, and then keeps the action already stored — but a deployment with no
 *   policy yet has nothing to keep, so its first write names one (the server
 *   answers `RETENTION_POLICY_ACTION_REQUIRED` otherwise).
 * - The three behaviour flags are optional. Omitted on a first write they take
 *   the database defaults; omitted on a later write they keep the stored value.
 *
 * There is no `organizationId` here and an unknown key is stripped, not
 * honoured: the organization comes from the caller's session, and the row's
 * organization is copied from the deployment it belongs to.
 */
export const UpsertDeploymentRetentionPolicyBodySchema = z
  .object({
    enabled: z.boolean(),
    action: ChatRetentionActionSchema.optional(),
    days: z.number().int().min(RETENTION_POLICY_DAYS_MIN).max(RETENTION_POLICY_DAYS_MAX),
    fireStatusEvents: z.boolean().optional(),
    skipOpenTickets: z.boolean().optional(),
    skipAssigned: z.boolean().optional()
  })
  .superRefine((body, ctx) => {
    if (body.enabled && body.action === undefined) {
      ctx.addIssue({
        code: "custom",
        path: ["action"],
        message: "An enabled retention policy needs an action: ARCHIVE, DELETE or SHRED."
      });
    }
  });

export type UpsertDeploymentRetentionPolicyBody = z.infer<
  typeof UpsertDeploymentRetentionPolicyBodySchema
>;

/**
 * A deployment's policy as the API returns it — the stored row, or the
 * defaults when none is stored.
 *
 * `configured` separates the two, because the defaults are a legitimate stored
 * value as well: a row saved as `enabled: false` with the default flags would
 * otherwise read exactly like a deployment nobody has configured. `action` and
 * `days` are `null` only while `configured` is false — the table carries no
 * default for either.
 */
export const DeploymentRetentionPolicyResponseSchema = z.object({
  deploymentId: z.string().uuid(),
  configured: z.boolean(),
  enabled: z.boolean(),
  action: ChatRetentionActionSchema.nullable(),
  days: z.number().int().nullable(),
  fireStatusEvents: z.boolean(),
  skipOpenTickets: z.boolean(),
  skipAssigned: z.boolean(),
  updatedAt: z.string().nullable()
});

export type DeploymentRetentionPolicyResponse = z.infer<
  typeof DeploymentRetentionPolicyResponseSchema
>;

/**
 * What a deployment with no stored policy reads as.
 *
 * The four booleans are the column defaults of `DeploymentRetentionPolicy`
 * (migration `20260915090000_add_deployment_retention_policy`), which is where
 * a default changes — never here first.
 */
export const DEPLOYMENT_RETENTION_POLICY_DEFAULTS = {
  enabled: false,
  action: null,
  days: null,
  fireStatusEvents: false,
  skipOpenTickets: true,
  skipAssigned: true
} as const satisfies Omit<
  DeploymentRetentionPolicyResponse,
  "deploymentId" | "configured" | "updatedAt"
>;
